import type { ElevationGrid } from '@roadworld/shared';

export interface DecodedElevation {
  width: number;
  height: number;
  south: number;
  west: number;
  north: number;
  east: number;
  values: Float32Array;
}

export function decodeElevation(grid: ElevationGrid): DecodedElevation {
  const bytes = base64ToBytes(grid.values);
  const values = new Float32Array(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength / Float32Array.BYTES_PER_ELEMENT,
  );
  return {
    width: grid.width,
    height: grid.height,
    south: grid.south,
    west: grid.west,
    north: grid.north,
    east: grid.east,
    values,
  };
}

export function encodeElevation(values: Float32Array): string {
  const bytes = new Uint8Array(values.buffer, values.byteOffset, values.byteLength);
  return bytesToBase64(bytes);
}

/**
 * Bilinear elevation lookup. `u` and `v` are normalized coordinates within the grid:
 * u in [0,1] runs west→east, v in [0,1] runs south→north.
 */
export function sampleElevationUV(grid: DecodedElevation, u: number, v: number): number {
  const cu = clamp(u, 0, 1);
  const cv = clamp(v, 0, 1);
  const fx = cu * (grid.width - 1);
  const fy = cv * (grid.height - 1);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(x0 + 1, grid.width - 1);
  const y1 = Math.min(y0 + 1, grid.height - 1);
  const tx = fx - x0;
  const ty = fy - y0;

  // Row 0 is south, row height-1 is north. (Grid is stored south-to-north.)
  const idx = (y: number, x: number) => y * grid.width + x;
  const h00 = grid.values[idx(y0, x0)] ?? 0;
  const h10 = grid.values[idx(y0, x1)] ?? 0;
  const h01 = grid.values[idx(y1, x0)] ?? 0;
  const h11 = grid.values[idx(y1, x1)] ?? 0;

  const a = h00 * (1 - tx) + h10 * tx;
  const b = h01 * (1 - tx) + h11 * tx;
  return a * (1 - ty) + b * ty;
}

export function sampleElevationLatLng(grid: DecodedElevation, lat: number, lng: number): number {
  const u = (lng - grid.west) / (grid.east - grid.west);
  const v = (lat - grid.south) / (grid.north - grid.south);
  return sampleElevationUV(grid, u, v);
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

// Universal base64 helpers (work in browser and Node) — Buffer is referenced via globalThis
// to keep this package free of @types/node.
interface BufferShim {
  from(input: string, encoding: string): { byteLength: number; buffer: ArrayBufferLike };
  from(buffer: ArrayBufferLike, offset: number, length: number): { toString(encoding: string): string };
}
function getNodeBuffer(): BufferShim | undefined {
  const g = globalThis as unknown as { Buffer?: BufferShim };
  return g.Buffer;
}

function base64ToBytes(b64: string): Uint8Array {
  const B = getNodeBuffer();
  if (B) {
    const buf = B.from(b64, 'base64') as unknown as Uint8Array;
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  const B = getNodeBuffer();
  if (B) {
    return B.from(bytes.buffer, bytes.byteOffset, bytes.byteLength).toString('base64');
  }
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}
